/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable global-require */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-shadow */

'use-strict';

import { ILocationService, IScope } from 'angular';
import { ChartData } from 'chart.js';
import { RootScope } from 'app.config';
import { NgTableParams } from 'ng-table';
import { TitleService } from 'src/app/shared/services/title';
import { FuelAPIService } from '../../../shared/api/fuel-api.service';
import { ConfigService, ConstantTypes } from '../../../shared/config/config.service';
import { InterfaceService } from '../../../shared/interface/interface.service';
import { FuelReceiptWithVehicle } from '../../../src/app/@shared-types/fuel';
import { UserRole } from '../../../src/app/@types/user';
import LogService from '../../../src/app/shared/services/log';
import { PermissionsService } from '../../authorization/permissions.service';
import { TableService } from '../../../shared/tables/table.service';

interface AnalyticsFilter {
  year: string | undefined;
  yearOptions: Record<string, string>;
}

interface VehicleRankEntry {
  vin: string;
  make: string;
  model: string;
  licence: string;
  totalCost: number;
  fillUps: number;
  avgConsumption: number | null;
}

interface AnalyticsState {
  ready: boolean;
  filter: AnalyticsFilter;
  monthlyCosts: ChartData;
  fuelTypes: ChartData;
  pricePerLitre: ChartData;
  vehicleRanking: VehicleRankEntry[];
}

interface TableFilter {
  year: string | undefined;
  yearOptions: Record<string, string>;
}

interface FuelTableScope extends IScope {
  table: NgTableParams<FuelReceiptWithVehicle>,
  receipts: FuelReceiptWithVehicle[] | null,
  tableFilter: TableFilter,
  analytics: AnalyticsState,
  exportDataset: () => void,
  removeAllFilter: () => void,
  removeFilter: (filter: string) => void,
  tableClick: (receiptId: number) => void,
  tableFilterChange: () => void,
  onTabChange: (item: string) => void,
  analyticsFilterChange: () => void,
}

// Maps hybrid variants to their underlying base fuel for cost grouping
const HYBRID_BASE_FUEL: Partial<Record<string, string>> = {
  HYBRID_PETROL_MILD: 'PETROL',
  HYBRID_PETROL_PLUGIN: 'PETROL',
  HYBRID_FULL: 'PETROL',
  HYBRID_DIESEL_MILD: 'DIESEL',
  HYBRID_DIESEL_PLUGIN: 'DIESEL',
};

const MONTH_KEYS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/* @ngInject */
export default function FuelTableController(
  $scope: FuelTableScope,
  $location: ILocationService,
  NgTableParams: any,
  tableService: TableService,
  fuelApiService: FuelAPIService,
  interfaceService: InterfaceService,
  permissionsService: PermissionsService,
  logService: LogService,
  configService: ConfigService,
): void {
  $scope.receipts = null;

  const currentYear = new Date().getFullYear();
  const tableYearOptions: Record<string, string> = {};
  for (let y = currentYear; y >= currentYear - 5; y -= 1) {
    tableYearOptions[String(y)] = String(y);
  }
  $scope.tableFilter = {
    year: String(currentYear),
    yearOptions: tableYearOptions,
  };

  $scope.analytics = {
    ready: false,
    filter: { year: String(new Date().getFullYear()), yearOptions: {} },
    monthlyCosts: { datasets: [] },
    fuelTypes: { datasets: [] },
    pricePerLitre: { datasets: [] },
    vehicleRanking: [],
  };

  let analyticsData: FuelReceiptWithVehicle[] | null = null;

  const iqrBounds = (values: number[]): { min: number; max: number } => {
    const sorted = [...values].sort((a, b) => a - b);
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const iqr = q3 - q1;
    return { min: q1 - 1.5 * iqr, max: q3 + 1.5 * iqr };
  };

  const computeAvgConsumption = (
    fills: Array<{ date: string; mileage: number; liter: number }>,
  ): number | null => {
    const sorted = [...fills].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const consumptions: number[] = [];
    for (let i = 1; i < sorted.length; i += 1) {
      const diff = sorted[i].mileage - sorted[i - 1].mileage;
      if (diff > 0 && sorted[i].liter > 0) {
        consumptions.push((sorted[i].liter / diff) * 100);
      }
    }
    return consumptions.length ? consumptions.reduce((a, b) => a + b, 0) / consumptions.length : null;
  };

  const computeAnalytics = (): void => {
    if (!analyticsData) return;

    // Build year options from all available data
    const availableYears = [...new Set(
      analyticsData.map((r) => String(new Date(r.date).getFullYear())),
    )].sort((a, b) => Number(b) - Number(a));
    $scope.analytics.filter.yearOptions = availableYears.reduce(
      (acc, y) => ({ ...acc, [y]: y }), {} as Record<string, string>,
    );

    // Current year receipts
    const receipts = $scope.analytics.filter.year
      ? analyticsData.filter(
        (r) => String(new Date(r.date).getFullYear()) === $scope.analytics.filter.year,
      )
      : analyticsData;

    // Previous year receipts for year-over-year comparison
    const prevYear = $scope.analytics.filter.year
      ? String(Number($scope.analytics.filter.year) - 1)
      : null;
    const prevReceipts = prevYear
      ? analyticsData.filter(
        (r) => String(new Date(r.date).getFullYear()) === prevYear,
      )
      : [];

    // Chart 1: Monthly costs with year-over-year overlay
    const buildMonthly = (recs: FuelReceiptWithVehicle[]): Map<string, number> => {
      const m = new Map<string, number>();
      recs.forEach((r) => {
        const key = String(new Date(r.date).getMonth() + 1).padStart(2, '0');
        m.set(key, (m.get(key) || 0) + r.cost);
      });
      return m;
    };
    const currentMonthly = buildMonthly(receipts);
    const prevMonthly = buildMonthly(prevReceipts);
    $scope.analytics.monthlyCosts = {
      labels: MONTH_LABELS,
      datasets: [
        {
          label: $scope.analytics.filter.year || 'Aktuell',
          data: MONTH_KEYS.map((k) => currentMonthly.get(k) || 0),
          borderColor: '#b48e4c',
        },
        ...(prevYear ? [{
          label: prevYear,
          data: MONTH_KEYS.map((k) => prevMonthly.get(k) || 0),
          borderColor: '#888888',
        }] : []),
      ],
    };

    // Chart 2: Fuel type distribution — hybrids collapsed to base fuel, labels in German
    const fuelLabels = configService.getConstant<Record<string, string>>(ConstantTypes.FUEL_TYPES) || {};
    const typeMap = new Map<string, number>();
    receipts.forEach((r) => {
      if (!r.fuelType) return;
      const base = HYBRID_BASE_FUEL[r.fuelType] || r.fuelType;
      typeMap.set(base, (typeMap.get(base) || 0) + r.cost);
    });
    const sortedTypes = [...typeMap.entries()].sort((a, b) => b[1] - a[1]);
    $scope.analytics.fuelTypes = {
      labels: sortedTypes.map(([type]) => fuelLabels[type] || type),
      datasets: [{
        label: 'Kosten (€)',
        data: sortedTypes.map(([, cost]) => cost),
      }],
    };

    // Chart 3: Price per litre — IQR outlier filter applied
    let withLiter = [...receipts]
      .filter((r) => r.liter > 0)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (withLiter.length >= 4) {
      const prices = withLiter.map((r) => r.cost / r.liter);
      const { min, max } = iqrBounds(prices);
      withLiter = withLiter.filter((r) => {
        const p = r.cost / r.liter;
        return p >= min && p <= max;
      });
    }
    $scope.analytics.pricePerLitre = {
      labels: withLiter.map((r) => new Date(r.date).toLocaleDateString('de-DE')),
      datasets: [{
        label: '€ / Liter',
        data: withLiter.map((r) => Math.round((r.cost / r.liter) * 100) / 100),
        borderColor: '#4c7ab4',
      }],
    };

    // Vehicle ranking — top 15 by total cost, with avg consumption (l/100km)
    const vehicleMap = new Map<string, {
      make: string;
      model: string;
      licence: string;
      totalCost: number;
      fillUps: number;
      fills: Array<{ date: string; mileage: number; liter: number }>;
    }>();
    receipts.forEach((r) => {
      if (!r.vin) return;
      const existing = vehicleMap.get(r.vin);
      if (existing) {
        existing.totalCost += r.cost;
        existing.fillUps += 1;
        existing.fills.push({ date: String(r.date), mileage: r.mileage, liter: r.liter });
      } else {
        vehicleMap.set(r.vin, {
          make: r.make || '',
          model: r.model || '',
          licence: r.licence || '',
          totalCost: r.cost,
          fillUps: 1,
          fills: [{ date: String(r.date), mileage: r.mileage, liter: r.liter }],
        });
      }
    });
    $scope.analytics.vehicleRanking = [...vehicleMap.entries()]
      .map(([vin, data]) => ({
        vin,
        make: data.make,
        model: data.model,
        licence: data.licence,
        totalCost: data.totalCost,
        fillUps: data.fillUps,
        avgConsumption: computeAvgConsumption(data.fills),
      }))
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, 15);

    $scope.analytics.ready = true;
    interfaceService.applyScope($scope);
  };

  const loadAnalyticsData = (): void => {
    $scope.analytics.ready = false;
    fuelApiService.getAll()
      .then((receipts) => {
        analyticsData = receipts;
        computeAnalytics();
      })
      .catch((e) => logService.error(e));
  };

  $scope.onTabChange = (item: string): void => {
    if (item !== 'Auswertung') return;
    if (analyticsData) {
      computeAnalytics();
    } else {
      loadAnalyticsData();
    }
  };

  $scope.analyticsFilterChange = (): void => {
    computeAnalytics();
  };

  const updateTableData = () => {
    if (!$scope.receipts) return;
    $scope.table.settings({
      dataset: $scope.receipts,
    });
    interfaceService.applyScope($scope);
  };

  const getReceipts = () => {
    $scope.receipts = null;
    const year = $scope.tableFilter.year ? parseInt($scope.tableFilter.year, 10) : undefined;
    fuelApiService.getAll(year)
      .then((receipts) => {
        $scope.receipts = receipts;
        updateTableData();
      })
      .catch((e) => logService.error(e));
  };

  $scope.tableFilterChange = (): void => {
    getReceipts();
  };

  const init = () => {
    $scope.table = new NgTableParams({
      sorting: {
        id: 'desc',
      },
    });

    getReceipts();
  };

  /**
   * Remove all applied filters from table
   */
  $scope.removeAllFilter = () => {
    $scope.table.filter({});
  };

  $scope.exportDataset = () => {
    if (!$scope.receipts) return;

    tableService.exportDataset<FuelReceiptWithVehicle>(
      $scope.receipts,
      'autoboard-kraftstoffbelege',
      undefined,
      undefined,
      require('./translations/fuel.json'),
    );
  };

  /**
   * Remove the supplied filter from the view's table.
   * @param {Object} filter
   */
  $scope.removeFilter = (filter: string) => {
    delete $scope.table.filter()[filter];
  };

  $scope.tableClick = (receiptId) => {
    if (!permissionsService.isPermitted([UserRole.SELLER_PLUS])) return;

    $location.path(`/sales/fuel/edit/${receiptId}`);
  };

  init();
}
